import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaStreams } from '../MytaStreamsProvider';
import { MytaverseLogger } from '@helpers/logger';
import {
  CINEMA_PHASES,
  ETA_REVISION_RATIO,
  ETA_REVISION_SEC,
  MAX_FRAME_DELTA_SEC,
  WORLD_ANNOUNCE_WAIT_MS,
  WORLD_SETTLE_MS,
} from './constants';
import {
  advancePace,
  createPace,
  describeOverdue,
  describeRemaining,
  ICinemaExpectation,
  ICinemaSegment,
  isOverdue,
  overdueSecOf,
  remainingTotalSec,
  segmentsOf,
} from './cinemaPace';

export interface ICinemaError {
  headline: string;
  reason: string;
}

export interface ICinemaFrame {
  value: number;
  phase: number;
  segments: ICinemaSegment[];
}

export type CinemaProgressListener = (frame: ICinemaFrame) => void;

interface IServerEta {
  key: string;
  valueSec: number;
  at: number;
}

interface IToldExpectation {
  expectation: ICinemaExpectation;
  told: boolean;
}

/**
 * Drives the MytaStreams loading-cinema progress bar and reports when entry is possible.
 *
 * @returns `percent`/`subscribe` for the animated progress bar, `phaseIdx`/`etaText` for the
 * loading copy, `ready`/`done` for gating entry, and `error`/`hintOverride`/`onSpot` for
 * messaging.
 */
export const useCinemaProgress = () => {
  const {
    leaseStatus,
    leasePhase,
    dataChannelOpened,
    mytaLoading,
    connectedSlot,
    lease,
  } = useMytaStreams();
  const { currentRoom, teleportFinished } = useMytaverseEvent();

  // The stream, and then the world. `streamUp` is the video track decoding into a known room — everything the
  // page needs, and not yet anything the user should be let into: UE is still placing the first runtime
  // objects behind it. `teleportFinished` is UE's own word that it is done (CLIENT_READY / TELEPORT_FINISHED
  // on this session, hooks/index.ts), and the loading runs until it arrives plus WORLD_SETTLE_MS.
  const streamUp = dataChannelOpened && !mytaLoading && !!currentRoom;

  const [announceWaitedOut, setAnnounceWaitedOut] = useState(false);
  const [settled, setSettled] = useState(false);

  const worldAnnounced = teleportFinished || announceWaitedOut;
  const ready = streamUp && worldAnnounced && settled;

  useEffect(() => {
    if (!streamUp || teleportFinished) {
      return;
    }

    const timeout = setTimeout(() => {
      MytaverseLogger.log(
        'UE never announced the world — finishing the loading on the stream alone',
        { waitedMs: WORLD_ANNOUNCE_WAIT_MS },
      );
      setAnnounceWaitedOut(true);
    }, WORLD_ANNOUNCE_WAIT_MS);

    return () => clearTimeout(timeout);
  }, [streamUp, teleportFinished]);

  useEffect(() => {
    if (!streamUp || !worldAnnounced) {
      return;
    }

    const timeout = setTimeout(() => setSettled(true), WORLD_SETTLE_MS);

    return () => clearTimeout(timeout);
  }, [streamUp, worldAnnounced]);

  // The GPU box behind this entry is a spot box — bought cheap and interruptible. Worth saying out loud on the
  // loading screen, because it is slower to get by construction (myta-streams D-49 buys spot before waking any
  // on-demand box) and an unexplained extra wait is indistinguishable from a hang. The lease is read first: the
  // provider clears `leasePhase` at the grant, so the two are never both set, and this keeps whichever answer
  // the server gave last. Absent on either means "no box chosen yet", never "an ordinary one".
  const onSpot = (lease?.market ?? leasePhase?.market) === 'spot';

  const phaseIdx = useMemo(() => {
    if (streamUp) {
      return 4;
    }

    if (dataChannelOpened) {
      return 3;
    }

    if (connectedSlot) {
      return 2;
    }

    if (leaseStatus === 'active') {
      return 1;
    }

    return 0;
  }, [streamUp, dataChannelOpened, connectedSlot, leaseStatus]);

  const error: ICinemaError | null = useMemo(() => {
    if (leaseStatus === 'error' || leaseStatus === 'unconfigured') {
      return {
        headline: 'We couldn’t start your experience',
        reason: 'Something went wrong while preparing your session',
      };
    }

    if (leasePhase?.phase === 'build_unsupported') {
      return {
        headline: 'This experience is temporarily unavailable',
        reason: 'It cannot start right now — please try again later',
      };
    }

    return null;
  }, [leaseStatus, leasePhase?.phase]);

  const hintOverride = useMemo(() => {
    if (leasePhase?.phase === 'already_seated') {
      return 'You are already in this experience in another tab — close it and this one will connect automatically.';
    }

    return null;
  }, [leasePhase?.phase]);

  const paceRef = useRef(createPace());
  const frameRef = useRef<ICinemaFrame>({
    value: 0,
    phase: 0,
    segments: segmentsOf(paceRef.current),
  });
  const phaseIdxRef = useRef(phaseIdx);
  const phaseStartedAtRef = useRef(performance.now());
  const readyRef = useRef(ready);
  const serverEtaRef = useRef<IServerEta | null>(null);
  const restartEtaRef = useRef<number | null>(null);
  const shownRemainingRef = useRef<number | null>(null);
  const listenersRef = useRef(new Set<CinemaProgressListener>());

  const [percent, setPercent] = useState(0);
  const [etaText, setEtaText] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    phaseIdxRef.current = phaseIdx;
    phaseStartedAtRef.current = performance.now();
  }, [phaseIdx]);

  const publish = useCallback((frame: ICinemaFrame) => {
    frameRef.current = frame;
    listenersRef.current.forEach((listener) => listener(frame));
  }, []);

  useEffect(() => {
    readyRef.current = ready;

    if (!ready) {
      return;
    }

    // Both latched from `ready` directly rather than left for the rAF loop below to reach: that
    // loop can freeze or throttle on a hidden tab, which otherwise stalled `done` (and the entry
    // it gates) for as long as the tab stayed hidden, and left the bar visibly stuck below 100
    // while entry had already proceeded.
    setDone(true);
    setPercent(100);
    setEtaText(null);
    publish({ ...frameRef.current, value: 100 });
  }, [ready, publish]);

  const serverPhase = leasePhase?.phase;
  const serverEtaSec = leasePhase?.etaSec;

  useEffect(() => {
    if (serverEtaSec == null) {
      serverEtaRef.current = null;
      return;
    }

    const key = `${serverPhase ?? '-'}:${serverEtaSec}`;

    if (serverEtaRef.current?.key === key) {
      return;
    }

    serverEtaRef.current = {
      key,
      valueSec: serverEtaSec,
      at: performance.now(),
    };
  }, [serverPhase, serverEtaSec]);

  const restartEtaSec = lease?.restarting ? lease.etaSec ?? null : null;

  useEffect(() => {
    restartEtaRef.current = restartEtaSec;
  }, [restartEtaSec]);

  const subscribe = useCallback((listener: CinemaProgressListener) => {
    listenersRef.current.add(listener);
    listener(frameRef.current);

    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (error) {
      return;
    }

    let frameId = 0;
    let previousAt = performance.now();

    const expectationNow = (): IToldExpectation => {
      const idx = phaseIdxRef.current;
      const since = phaseStartedAtRef.current;
      const serverEta = serverEtaRef.current;

      if (idx === 0 && serverEta) {
        return {
          expectation: { sec: serverEta.valueSec, fromMs: serverEta.at },
          told: true,
        };
      }

      if (idx === 1 && restartEtaRef.current) {
        return {
          expectation: { sec: restartEtaRef.current, fromMs: since },
          told: true,
        };
      }

      return {
        expectation: { sec: CINEMA_PHASES[idx].expectedSec, fromMs: since },
        told: idx > 0,
      };
    };

    const etaTextNow = (
      now: number,
      idx: number,
      { expectation, told }: IToldExpectation,
    ): string | null => {
      if (!told || isOverdue(expectation, now)) {
        shownRemainingRef.current = null;

        return told
          ? describeOverdue(idx, overdueSecOf(expectation, now))
          : null;
      }

      const remaining = remainingTotalSec(idx, expectation, now);
      const shown = shownRemainingRef.current;
      const next =
        shown === null ||
        remaining < shown ||
        remaining > shown * ETA_REVISION_RATIO + ETA_REVISION_SEC
          ? remaining
          : shown;
      shownRemainingRef.current = next;

      return describeRemaining(next);
    };

    const frame = () => {
      const now = performance.now();
      const deltaSec = Math.min(
        MAX_FRAME_DELTA_SEC,
        Math.max(0, (now - previousAt) / 1000),
      );
      previousAt = now;

      if (readyRef.current) {
        return;
      }

      const idx = phaseIdxRef.current;
      const told = expectationNow();
      const pace = advancePace(paceRef.current, {
        phaseIdx: idx,
        expectation: told.expectation,
        nowMs: now,
        deltaSec,
      });
      paceRef.current = pace;
      publish({
        value: pace.value,
        phase: pace.phase,
        segments: segmentsOf(pace),
      });

      const nextPercent = Math.floor(pace.value);
      const nextEta = etaTextNow(now, idx, told);

      setPercent((prev) => (prev === nextPercent ? prev : nextPercent));
      setEtaText((prev) => (prev === nextEta ? prev : nextEta));

      frameId = requestAnimationFrame(frame);
    };

    frameId = requestAnimationFrame(frame);

    return () => cancelAnimationFrame(frameId);
  }, [error, publish]);

  return {
    percent,
    subscribe,
    phaseIdx,
    ready,
    done,
    etaText,
    error,
    hintOverride,
    onSpot,
  };
};
